import { defaultCountries } from "react-international-phone";
import { parsePhoneNumber } from "react-phone-number-input";

const LINKING_PHONE_KEY = "mincool.prefilledPhoneNumber";
const LINKING_PHONE_PARAM_KEYS = ["phone", "phoneNumber", "pn"];
const FALLBACK_COUNTRY = "za";
const SUPPORTED_COUNTRIES = new Set(defaultCountries.map((country) => country[1]));
const DIAL_CODE_BY_ISO2 = new Map(
  defaultCountries.map(([, iso2, dialCode]) => [iso2, dialCode]),
);

function inferCountryFromDialCode(phoneNumber) {
  const normalizedValue = normalizeLinkingPhonePrefill(phoneNumber);
  if (!normalizedValue.startsWith("+")) {
    return "";
  }

  const digits = normalizedValue.slice(1);
  let matchedCountry = "";
  let matchedDialCodeLength = 0;

  for (const [, iso2, dialCode] of defaultCountries) {
    if (
      digits.startsWith(dialCode)
      && dialCode.length > matchedDialCodeLength
      && SUPPORTED_COUNTRIES.has(iso2)
    ) {
      matchedCountry = iso2;
      matchedDialCodeLength = dialCode.length;
    }
  }

  return matchedCountry;
}

function normalizePhoneCountryHint(value) {
  if (typeof value !== "string") {
    return "";
  }

  const normalized = value.trim().toLowerCase();
  if (!/^[a-z]{2}$/.test(normalized)) {
    return "";
  }

  return SUPPORTED_COUNTRIES.has(normalized) ? normalized : "";
}

function getSessionStorage() {
  if (typeof window === "undefined") {
    return null;
  }
  return window.sessionStorage;
}

function getNavigatorLocales() {
  if (typeof navigator === "undefined") {
    return [];
  }
  const locales = [];
  if (Array.isArray(navigator.languages)) {
    locales.push(...navigator.languages);
  }
  if (navigator.language) {
    locales.push(navigator.language);
  }
  return locales.filter(Boolean);
}

function getNavigatorTimeZone() {
  if (typeof Intl === "undefined" || typeof Intl.DateTimeFormat !== "function") {
    return "";
  }

  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

function extractRegionCandidate(locale) {
  if (typeof locale !== "string") {
    return "";
  }
  const parts = locale
    .split(/[-_]/)
    .map((part) => part.trim())
    .filter(Boolean);

  for (const part of parts.slice(1)) {
    if (/^[a-z]{2}$/i.test(part)) {
      return part.toLowerCase();
    }
  }

  return "";
}

export function normalizeLinkingPhonePrefill(rawValue) {
  if (typeof rawValue !== "string") {
    return "";
  }

  const compactValue = rawValue
    .trim()
    .replace(/^tel:/i, "")
    .replace(/^whatsapp:/i, "")
    .replace(/[^\d+]/g, "");

  if (!compactValue) {
    return "";
  }

  if (compactValue.startsWith("00")) {
    return `+${compactValue.slice(2)}`;
  }

  if (compactValue.startsWith("+")) {
    return `+${compactValue.slice(1).replace(/[^\d]/g, "")}`;
  }

  return compactValue.replace(/[^\d]/g, "");
}

export function getLinkingPhonePrefillFromSearch(search = "") {
  const searchParams = new URLSearchParams(search);
  for (const key of LINKING_PHONE_PARAM_KEYS) {
    const phoneNumber = normalizeLinkingPhonePrefill(searchParams.get(key) || "");
    if (phoneNumber) {
      return phoneNumber;
    }
  }
  return "";
}

export function rememberLinkingPhonePrefill(phoneNumber) {
  const store = getSessionStorage();
  if (!store) {
    return;
  }

  const phoneState = getLinkingPhoneState(phoneNumber);
  const normalizedPhoneNumber = phoneState.canSubmit
    ? phoneState.normalizedPhoneNumber
    : normalizeLinkingPhonePrefill(phoneNumber);
  if (!normalizedPhoneNumber) {
    store.removeItem(LINKING_PHONE_KEY);
    return;
  }

  store.setItem(LINKING_PHONE_KEY, normalizedPhoneNumber);
}

export function clearLinkingPhonePrefill() {
  const store = getSessionStorage();
  if (!store) {
    return;
  }
  store.removeItem(LINKING_PHONE_KEY);
}

export function captureLinkingPhonePrefillFromCurrentUrl() {
  if (typeof window === "undefined") {
    return "";
  }

  const phoneNumber = getLinkingPhonePrefillFromSearch(window.location.search);
  if (phoneNumber) {
    rememberLinkingPhonePrefill(phoneNumber);
  }
  return phoneNumber;
}

export function getRememberedLinkingPhonePrefill() {
  const fromUrl = captureLinkingPhonePrefillFromCurrentUrl();
  if (fromUrl) {
    return fromUrl;
  }

  const store = getSessionStorage();
  if (!store) {
    return "";
  }

  return normalizeLinkingPhonePrefill(store.getItem(LINKING_PHONE_KEY) || "");
}

export function shouldAutoAdvanceLinking(phoneNumber) {
  return getLinkingPhoneState(phoneNumber).canSubmit;
}

export function inferDefaultPhoneCountry(
  locales = getNavigatorLocales(),
  hintedCountry = "",
  timeZone = getNavigatorTimeZone(),
) {
  const normalizedHint = normalizePhoneCountryHint(hintedCountry);
  if (normalizedHint) {
    return normalizedHint;
  }

  if (timeZone === "Africa/Johannesburg") {
    return "za";
  }

  for (const locale of locales) {
    const region = extractRegionCandidate(locale);
    if (region && SUPPORTED_COUNTRIES.has(region)) {
      return region;
    }
  }
  return FALLBACK_COUNTRY;
}

export function inferPhoneInputCountry(phoneNumber, fallbackCountry = "") {
  const phoneState = getLinkingPhoneState(phoneNumber);
  if (phoneState.countryIso2) {
    return phoneState.countryIso2;
  }

  const inferredCountry = inferCountryFromDialCode(phoneNumber);
  if (inferredCountry) {
    return inferredCountry;
  }

  const normalizedFallback = normalizePhoneCountryHint(fallbackCountry);
  if (normalizedFallback) {
    return normalizedFallback;
  }

  return FALLBACK_COUNTRY;
}

export function buildLinkingPhoneCanonical(inputValue, selectedCountry) {
  if (typeof inputValue !== "string") {
    return "";
  }

  const trimmedValue = inputValue.trim();
  if (!trimmedValue) {
    return "";
  }

  const normalizedValue = normalizeLinkingPhonePrefill(trimmedValue);
  if (!normalizedValue) {
    return "";
  }

  if (trimmedValue.startsWith("+")) {
    return normalizedValue;
  }

  const dialCode = DIAL_CODE_BY_ISO2.get(selectedCountry);
  if (!dialCode) {
    return normalizedValue;
  }

  if (normalizedValue.startsWith(dialCode)) {
    return `+${normalizedValue}`;
  }

  return `+${dialCode}${normalizedValue.replace(/^0/, "")}`;
}

export function deriveLinkingPhoneDisplayValue(phoneNumber, fallbackCountry) {
  const normalizedValue = normalizeLinkingPhonePrefill(phoneNumber);
  if (!normalizedValue) {
    return "";
  }

  const countryIso2 = inferPhoneInputCountry(normalizedValue, fallbackCountry);
  const dialCode = DIAL_CODE_BY_ISO2.get(countryIso2) || "";
  const candidateValue =
    !normalizedValue.startsWith("+") && dialCode && normalizedValue.startsWith(dialCode)
      ? `+${normalizedValue}`
      : normalizedValue;

  let parsedPhoneNumber;
  try {
    parsedPhoneNumber = parsePhoneNumber(candidateValue);
  } catch {
    parsedPhoneNumber = undefined;
  }

  if (parsedPhoneNumber?.country?.toLowerCase() === countryIso2) {
    return parsedPhoneNumber.formatNational();
  }

  return candidateValue;
}

export function deriveMaskedLinkingPhoneLabel(phoneNumber, fallbackCountry) {
  const normalizedValue = normalizeLinkingPhonePrefill(phoneNumber);
  if (!normalizedValue) {
    return "";
  }

  const displayValue = deriveLinkingPhoneDisplayValue(normalizedValue, fallbackCountry) || normalizedValue;
  let revealedDigits = 0;

  return [...displayValue].reverse().map((character) => {
    if (!/\d/.test(character)) {
      return character;
    }
    revealedDigits += 1;
    return revealedDigits <= 4 ? character : "•";
  }).reverse().join("");
}

function getFlagEmoji(countryIso2) {
  const normalized = String(countryIso2 || "").trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(normalized)) {
    return "";
  }
  return String.fromCodePoint(...[...normalized].map((letter) => 127397 + letter.charCodeAt(0)));
}

export function deriveMaskedInternationalLinkingPhoneLabel(phoneNumber, fallbackCountry) {
  const normalizedValue = normalizeLinkingPhonePrefill(phoneNumber);
  if (!normalizedValue) {
    return "";
  }

  const countryIso2 = inferPhoneInputCountry(normalizedValue, fallbackCountry);
  const dialCode = DIAL_CODE_BY_ISO2.get(countryIso2);
  const digits = normalizedValue.replace(/\D/g, "");
  const suffix = digits.slice(-4);
  const flagEmoji = getFlagEmoji(countryIso2);

  return [flagEmoji, dialCode ? `+${dialCode}` : "", "·· ···", suffix].filter(Boolean).join(" ");
}

export function getLinkingPhoneState(phoneNumber) {
  const normalizedPhoneNumber = normalizeLinkingPhonePrefill(phoneNumber);
  if (!normalizedPhoneNumber) {
    return {
      canSubmit: false,
      countryIso2: "",
      isPossible: false,
      isValid: false,
      normalizedPhoneNumber: "",
      reason: "empty",
    };
  }

  let candidatePhoneNumber = normalizedPhoneNumber;
  let parsedPhoneNumber;

  if (!normalizedPhoneNumber.startsWith("+")) {
    const inferredCountry = inferCountryFromDialCode(`+${normalizedPhoneNumber}`);
    if (inferredCountry) {
      try {
        parsedPhoneNumber = parsePhoneNumber(`+${normalizedPhoneNumber}`);
        candidatePhoneNumber = `+${normalizedPhoneNumber}`;
      } catch {
        parsedPhoneNumber = undefined;
      }
    }
  } else {
    try {
      parsedPhoneNumber = parsePhoneNumber(normalizedPhoneNumber);
    } catch {
      parsedPhoneNumber = undefined;
    }
  }

  if (!candidatePhoneNumber.startsWith("+") || !parsedPhoneNumber) {
    return {
      canSubmit: false,
      countryIso2: "",
      isPossible: false,
      isValid: false,
      normalizedPhoneNumber,
      reason: "missing_country_code",
    };
  }

  const countryIso2 = parsedPhoneNumber?.country?.toLowerCase() || "";
  const isPossible = Boolean(parsedPhoneNumber?.isPossible());
  const isValid = Boolean(parsedPhoneNumber?.isValid());
  const canonicalNumber = parsedPhoneNumber?.number || candidatePhoneNumber;
  const canSubmit = Boolean(countryIso2) && isPossible;

  return {
    canSubmit,
    countryIso2,
    isPossible,
    isValid,
    normalizedPhoneNumber: canonicalNumber,
    reason: canSubmit ? "ready" : "invalid",
  };
}

export function buildLinkingPath({ code, phoneNumber, pathname = "/link" } = {}) {
  const searchParams = new URLSearchParams();
  if (code) {
    searchParams.set("ref", code);
  }

  const phoneState = getLinkingPhoneState(phoneNumber);
  const normalizedPhoneNumber = phoneState.canSubmit
    ? phoneState.normalizedPhoneNumber
    : normalizeLinkingPhonePrefill(phoneNumber);
  if (normalizedPhoneNumber) {
    searchParams.set("phone", normalizedPhoneNumber);
  }

  const query = searchParams.toString();
  return `${pathname}${query ? `?${query}` : ""}`;
}
